import React from "react";
import CheckIcon from "@material-ui/icons/Check";
import ScheduleIcon from "@material-ui/icons/Schedule";
import CloseIcon from "@material-ui/icons/Close";

import { FB } from "../../assets/feedbackTheme";
import { Stage, STAGE_LABEL } from "./tracking";

// Small presentational pieces shared by the table and the detail drawer.

export const STAGE_COLORS = {
  [Stage.Done]: { color: FB.done, bg: FB.doneSoft, border: FB.doneBorder },
  [Stage.Progress]: { color: FB.open, bg: FB.openSoft, border: FB.openBorder },
  [Stage.Missing]: { color: FB.textMuted, bg: FB.surfaceAlt, border: FB.border },
};

const STAGE_ICON = {
  [Stage.Done]: CheckIcon,
  [Stage.Progress]: ScheduleIcon,
  [Stage.Missing]: CloseIcon,
};

// Done ticks are solid header-teal with a white, thickened check so they read at a
// glance; progress / missing keep the soft tones.
const DONE_TICK = { color: FB.surface, bg: FB.accent, border: FB.accent };

export const StageIcon = ({ stage, label, size = 28, round = false }) => {
  const done = stage === Stage.Done;
  const c = done ? DONE_TICK : STAGE_COLORS[stage];
  const Icon = STAGE_ICON[stage];
  return (
    <span
      title={`${label}: ${STAGE_LABEL[stage]}`}
      aria-label={`${label}: ${STAGE_LABEL[stage]}`}
      style={{
        width: size,
        height: size,
        flex: "0 0 auto",
        display: "inline-grid",
        placeItems: "center",
        borderRadius: round ? "50%" : 8,
        color: c.color,
        backgroundColor: c.bg,
        border: `1px solid ${c.border}`,
      }}
    >
      <Icon
        style={{
          fontSize: Math.round(size * (done ? 0.62 : 0.55)),
          // MUI icons are filled paths; a same-color stroke makes the check bolder.
          ...(done ? { stroke: "currentColor", strokeWidth: 1.6 } : {}),
        }}
      />
    </span>
  );
};

export const StagePill = ({ stage, label }) => {
  const c = STAGE_COLORS[stage];
  const Icon = STAGE_ICON[stage];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 9px 2px 6px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
        color: c.color,
        backgroundColor: c.bg,
        border: `1px solid ${c.border}`,
      }}
    >
      <Icon style={{ fontSize: 14 }} />
      {label}
    </span>
  );
};

export const CountChip = ({ value, goal, stage }) => {
  const c = STAGE_COLORS[stage];
  return (
    <span
      style={{
        minWidth: 34,
        height: 26,
        padding: "0 8px",
        boxSizing: "border-box",
        display: "inline-flex",
        alignItems: "baseline",
        justifyContent: "center",
        gap: 1,
        lineHeight: "24px",
        borderRadius: 7,
        fontSize: 13,
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        color: c.color,
        backgroundColor: c.bg,
        border: `1px solid ${c.border}`,
      }}
    >
      {Number(value || 0)}
      {goal != null && <small style={{ fontSize: 10, opacity: 0.75 }}>/{goal}</small>}
    </span>
  );
};

export const OverallBadge = ({ done }) => {
  const c = STAGE_COLORS[done ? Stage.Done : Stage.Progress];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: c.color,
        backgroundColor: c.bg,
        border: `1px solid ${c.border}`,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: c.color }} />
      {done ? "Done" : "In Progress"}
    </span>
  );
};

export const ProgressBar = ({ value }) => {
  const safe = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <span
      aria-label={`Genel ilerleme yüzde ${safe}`}
      style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 92 }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 48,
          height: 6,
          borderRadius: 999,
          overflow: "hidden",
          backgroundColor: FB.border,
        }}
      >
        <span
          style={{
            display: "block",
            width: `${safe}%`,
            height: "100%",
            borderRadius: "inherit",
            backgroundColor: FB.accentMid,
            transition: "width .3s ease",
          }}
        />
      </span>
      <span style={{ fontSize: 12, fontWeight: 600, color: FB.textSecondary, fontVariantNumeric: "tabular-nums" }}>
        %{safe}
      </span>
    </span>
  );
};
